/**
 * Platform → Pipelines.
 *
 * A pipeline is a label on connectors and workflows that already exist, plus
 * one derived answer to "did this flow work last night?" — a question that
 * currently means opening a connector card per step and a workflow execution
 * list, and joining them in your head.
 *
 * This page exists because the capability shipped inert: the API was there, but
 * declaring a pipeline meant a hand-written INSERT into two tables. Platform
 * admin only, matching the API, which refuses everyone else regardless of what
 * the UI shows.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  GitBranch,
  Loader2,
  Plus,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import {
  connectorNamesById,
  connectorOptionLabel,
  coverageSummary,
  createPipeline,
  editableConnectors,
  deletePipeline,
  fetchB2CClientForOrg,
  fetchPipelineCoverage,
  fetchOrgConnectors,
  fetchOrgWorkflows,
  replacePipelineSteps,
  selectableConnectors,
  slugify,
  stepsToDrafts,
  suggestSteps,
  fetchPipelineHealth,
  fetchPipelineMembership,
  fetchPipelines,
  healthLabel,
  healthTone,
  stepTarget,
  updatePipeline,
  type Pipeline,
  type PipelineClientRef,
  type PipelineCoverage,
  type PipelineHealth,
  type PipelineMembership,
  type StepDraft,
  type StepHealth,
  type StepType,
  type WorkflowSummary,
} from "@/lib/portalData.pipelines";
import type { B2CClientConnector } from "@/types/portal";

export const Route = createFileRoute("/portal/platform/pipelines")({
  component: PipelinesAdminPage,
});

// ---------------------------------------------------------------------------

function HealthPill({ health }: { health: StepHealth }) {
  const tone = healthTone(health);
  const cls = {
    good: "bg-emerald-100 text-emerald-800 border-emerald-200",
    bad: "bg-red-100 text-red-800 border-red-200",
    warn: "bg-amber-100 text-amber-800 border-amber-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
  }[tone];
  const Icon = {
    good: CheckCircle2,
    bad: XCircle,
    warn: AlertTriangle,
    neutral: Clock,
  }[tone];

  return (
    <Badge
      variant="outline"
      className={`gap-1 ${cls}`}
      data-testid={`health-${health}`}
    >
      <Icon className="h-3 w-3" />
      {healthLabel(health)}
    </Badge>
  );
}

function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
      data-testid="pipeline-error"
    >
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Declare form
// ---------------------------------------------------------------------------

const EMPTY_STEP = (order: number): StepDraft => ({
  step_order: order,
  stage_index: order - 1,
  step_type: "Connector",
  client_connector_id: null,
  workflow_key: "",
  transform_ref: "",
  expected_cadence: "",
});

/**
 * The org's connectors and workflows — everything a step can point at.
 *
 * Shared by the declare form and the edit panel: an edit that offered a
 * different set from a declaration would be its own kind of trap.
 */
function useOrgPickers(token: string, organisationId: string) {
  const [connectors, setConnectors] = useState<B2CClientConnector[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [conns, wfs] = await Promise.all([
          fetchOrgConnectors(token, organisationId),
          fetchOrgWorkflows(token),
        ]);
        if (cancelled) return;
        setConnectors(conns);
        setWorkflows(wfs);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setConnectors([]);
        setWorkflows([]);
        setError(
          e instanceof Error
            ? e.message
            : "Could not load this org's connectors and workflows.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, organisationId]);

  const options = useMemo(() => selectableConnectors(connectors), [connectors]);
  const namesById = useMemo(() => connectorNamesById(connectors), [connectors]);

  return { connectors, workflows, options, namesById, loading, error };
}

/** The step table. Identical in the declare form and the edit panel. */
function StepRows({
  steps,
  options,
  workflows,
  onPatch,
  onRemove,
}: {
  steps: StepDraft[];
  options: B2CClientConnector[];
  workflows: WorkflowSummary[];
  onPatch: (index: number, patch: Partial<StepDraft>) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <>
      {steps.map((step, i) => (
        <div
          key={i}
          className="grid items-end gap-2 rounded border border-slate-200 p-3 md:grid-cols-12"
          data-testid={`step-row-${i}`}
        >
          <div className="md:col-span-2">
            <Label className="text-xs">Stage</Label>
            <Input
              type="number"
              min={0}
              value={step.stage_index}
              data-testid={`step-${i}-stage`}
              onChange={(e) => onPatch(i, { stage_index: Number(e.target.value) })}
            />
          </div>

          <div className="md:col-span-3">
            <Label className="text-xs">Type</Label>
            <Select
              value={step.step_type}
              onValueChange={(v) => onPatch(i, { step_type: v as StepType })}
            >
              <SelectTrigger data-testid={`step-${i}-type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Connector">Connector</SelectItem>
                <SelectItem value="Workflow">Workflow</SelectItem>
                <SelectItem value="Transform">Transform</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-4">
            <Label className="text-xs">
              {step.step_type === "Connector"
                ? "Connector"
                : step.step_type === "Workflow"
                  ? "Workflow"
                  : "Transform ref"}
            </Label>

            {step.step_type === "Connector" && (
              <Select
                value={
                  step.client_connector_id != null
                    ? String(step.client_connector_id)
                    : ""
                }
                onValueChange={(v) => onPatch(i, { client_connector_id: Number(v) })}
              >
                <SelectTrigger data-testid={`step-${i}-connector`}>
                  <SelectValue placeholder="Pick a connector" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((c) => (
                    <SelectItem key={c.Id} value={String(c.Id)}>
                      {connectorOptionLabel(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {step.step_type === "Workflow" && (
              <Select
                value={step.workflow_key ?? ""}
                onValueChange={(v) => onPatch(i, { workflow_key: v })}
              >
                <SelectTrigger data-testid={`step-${i}-workflow`}>
                  <SelectValue placeholder="Pick a workflow" />
                </SelectTrigger>
                <SelectContent>
                  {workflows.map((w) => (
                    <SelectItem key={w.workflow_key} value={w.workflow_key}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {step.step_type === "Transform" && (
              <Input
                data-testid={`step-${i}-target`}
                value={step.transform_ref ?? ""}
                onChange={(e) => onPatch(i, { transform_ref: e.target.value })}
                placeholder="usp_TransformFuel"
              />
            )}
          </div>

          <div className="md:col-span-2">
            <Label
              className="text-xs"
              title="Optional cron. The only staleness signal for anything without a schedule of its own."
            >
              Expected cadence
            </Label>
            <Input
              data-testid={`step-${i}-cadence`}
              value={step.expected_cadence ?? ""}
              onChange={(e) => onPatch(i, { expected_cadence: e.target.value })}
              placeholder="0 3 * * *"
            />
          </div>

          <div className="md:col-span-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onRemove(i)}
              disabled={steps.length === 1}
              data-testid={`step-${i}-remove`}
              aria-label={`Remove step ${i + 1}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </>
  );
}

/** Shared step-list state: patch, append, remove-and-renumber. */
function useStepDrafts(initial: StepDraft[]) {
  const [steps, setSteps] = useState<StepDraft[]>(initial);

  const patchStep = (index: number, patch: Partial<StepDraft>) =>
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  // A new step lands one stage past the LAST stage, not one past its own
  // position: a real pipeline fans in (Haesloop reads three sources at stage 0),
  // so numbering by position would put an added step at stage 3 of a 0/0/1
  // pipeline and leave a hole at 2.
  const addStep = () =>
    setSteps((prev) => {
      const nextStage =
        prev.length === 0 ? 0 : Math.max(...prev.map((s) => s.stage_index)) + 1;
      return [
        ...prev,
        { ...EMPTY_STEP(prev.length + 1), stage_index: nextStage },
      ];
    });

  // Renumbering matters: the server requires a contiguous step_order, so
  // deleting from the middle without this is a guaranteed 400.
  const removeStep = (index: number) =>
    setSteps((prev) =>
      prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_order: i + 1 })),
    );

  return { steps, setSteps, patchStep, addStep, removeStep };
}

function DeclareForm({
  token,
  organisationId,
  organisationName,
  client,
  onCreated,
  onCancel,
}: {
  token: string;
  organisationId: string;
  organisationName: string;
  /** Resolved by the page. Derived, not picked — one b2c client per org. */
  client: PipelineClientRef | null;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const clientId = client?.Id ?? null;
  const {
    connectors,
    workflows,
    options,
    loading: loadingCtx,
    error: ctxError,
  } = useOrgPickers(token, organisationId);

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyEdited, setKeyEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [cadence, setCadence] = useState("");
  const { steps, setSteps, patchStep, addStep, removeStep } = useStepDrafts([
    EMPTY_STEP(1),
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setNameAndKey = (value: string) => {
    setName(value);
    // The key is a new identifier, so there is nothing to pick from — suggest
    // one from the name instead, and stop suggesting once it is hand-edited.
    if (!keyEdited) setKey(slugify(value));
  };

  const applySuggestion = () => setSteps(suggestSteps(connectors, workflows));

  const submit = async () => {
    setError(null);
    // Type narrowing, not a guard: the page does not open this form without a
    // client, and switching org closes it. Nothing reaches here with a null.
    if (clientId == null) return;
    setSaving(true);
    try {
      await createPipeline(token, {
        client_id: clientId,
        organisation_id: organisationId,
        name,
        pipeline_key: key,
        description,
        expected_cadence: cadence,
        steps,
      });
      onCreated();
    } catch (e) {
      // Verbatim: the server names the step and the fix ("connector 11 is
      // already in 'x'", "connector(s) not ACTIVE: 14 is DISCONNECTED").
      setError(e instanceof Error ? e.message : "Could not declare the pipeline.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="declare-form">
      <CardHeader>
        <CardTitle>Declare a pipeline for {organisationName}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className="rounded border border-slate-200 bg-slate-50 p-3 text-sm"
          data-testid="org-context"
        >
          {loadingCtx ? (
            <span className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading {organisationName}…
            </span>
          ) : clientId == null ? (
            <span className="text-amber-800" data-testid="no-client">
              {organisationName} has no b2c client, so there are no connectors to
              group into a pipeline.
            </span>
          ) : (
            <span className="text-slate-600">
              b2c client <strong>{client?.Name || clientId}</strong> ·{" "}
              {options.length} active connector{options.length === 1 ? "" : "s"} ·{" "}
              {workflows.length} workflow{workflows.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              data-testid="field-name"
              value={name}
              onChange={(e) => setNameAndKey(e.target.value)}
              placeholder="Fuel to Xero"
            />
          </div>
          <div>
            <Label htmlFor="key">Key</Label>
            <Input
              id="key"
              data-testid="field-key"
              value={key}
              onChange={(e) => {
                setKeyEdited(true);
                setKey(e.target.value);
              }}
              placeholder="fuel-to-xero"
            />
            <p className="mt-1 text-xs text-slate-500">
              What a workflow references. Suggested from the name, and fixed once
              declared.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              data-testid="field-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Fuel feed + the sync workflow that invoices to Xero"
            />
          </div>
          <div>
            <Label htmlFor="cadence">Expected cadence</Label>
            <Input
              id="cadence"
              data-testid="field-cadence"
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              placeholder="0 * * * *"
            />
            <p className="mt-1 text-xs text-slate-500">
              Optional cron for the whole flow. Covers steps with no schedule of
              their own — without it they can never be judged late. A step's own
              cadence, and a connector&apos;s own cron, both win over this.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label>Steps</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={applySuggestion}
                disabled={loadingCtx || options.length === 0}
                data-testid="suggest-steps"
                title="Lays out this org's scheduled connectors and workflows as a starting point. It does not infer how they connect — correct it before declaring."
              >
                <Sparkles className="mr-1 h-3 w-3" /> Suggest a shape
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addStep}
                data-testid="add-step"
              >
                <Plus className="mr-1 h-3 w-3" /> Add step
              </Button>
            </div>
          </div>

          <StepRows
            steps={steps}
            options={options}
            workflows={workflows}
            onPatch={patchStep}
            onRemove={removeStep}
          />
        </div>

        <ErrorNote message={error ?? ctxError} />

        <div className="flex gap-2">
          <Button
            onClick={submit}
            disabled={saving || loadingCtx || clientId == null}
            data-testid="submit-declare"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Declare
          </Button>
          <Button variant="outline" onClick={onCancel} data-testid="cancel-declare">
            Cancel
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          Declared as <strong>Draft</strong>. A new declaration is not a claim
          about production until you activate it.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Edit a declared pipeline's shape.
 *
 * The whole list is replaced in one PUT rather than patched step by step: the
 * shape IS the declaration, and an incremental edit would allow a half-applied
 * reorder that violates step_order uniqueness partway through. The server takes
 * the same view, so this mirrors it rather than fighting it.
 *
 * Name, key and status are not edited here — status has its own buttons, and the
 * key is what a workflow references, so changing it silently would break the
 * `pipeline_key` a DSL declares.
 */
function EditSteps({
  token,
  pipelineId,
  version,
  initial,
  connectors,
  workflows,
  onSaved,
  onCancel,
}: {
  token: string;
  pipelineId: string;
  /** The version this edit was composed against — sent as If-Match. */
  version: number;
  initial: StepDraft[];
  /** The org's FULL list, not just the selectable ones — see editableConnectors. */
  connectors: B2CClientConnector[];
  workflows: WorkflowSummary[];
  onSaved: (membership: PipelineMembership) => void;
  onCancel: () => void;
}) {
  const { steps, patchStep, addStep, removeStep } = useStepDrafts(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A step already pointing at a non-ACTIVE connector must still show it. The
  // pipeline's own steps are the only thing that can widen the list, so this
  // keys off the LIVE drafts, not the initial ones — otherwise re-picking a
  // stale step would drop the option the other stale step still needs.
  const pickable = useMemo(
    () => editableConnectors(connectors, steps),
    [connectors, steps],
  );

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      onSaved(await replacePipelineSteps(token, pipelineId, steps, version));
    } catch (e) {
      // Verbatim — the server names the step and the fix, e.g. "connector 479
      // is already in 'other-pipeline'".
      setError(e instanceof Error ? e.message : "Could not save the steps.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded border border-slate-300 p-3" data-testid="edit-steps-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>Steps</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addStep}
          data-testid="add-step"
        >
          <Plus className="mr-1 h-3 w-3" /> Add step
        </Button>
      </div>

      <StepRows
        steps={steps}
        options={pickable}
        workflows={workflows}
        onPatch={patchStep}
        onRemove={removeStep}
      />

      <ErrorNote message={error} />

      <div className="flex gap-2">
        <Button onClick={() => void save()} disabled={saving} data-testid="save-steps">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save steps
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="cancel-steps">
          Cancel
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Replaces the whole list. Connectors and workflows themselves are not
        touched — this only changes what the pipeline claims to be made of.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

/**
 * The pipeline's default cadence, editable in place.
 *
 * Lives on the detail rather than behind "Edit steps" because it is metadata
 * about the FLOW, not part of its shape — and because it is the one field that
 * decides whether a step with no cron of its own can be judged late at all.
 * Shown even when unset, since "no default" is exactly the state someone needs
 * to notice.
 */
function CadenceEditor({
  token,
  pipeline,
  canEdit,
  onSaved,
}: {
  token: string;
  pipeline: Pipeline;
  canEdit: boolean;
  /** Receives the pipeline the server returned, bumped version included. */
  onSaved: (updated: Pipeline) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(pipeline.expected_cadence ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A different pipeline is a different cadence — never carry one over.
  //
  // Keyed on the pipeline ID *only*, deliberately. Keying on the cadence value
  // as well meant any refresh that returned a different one — a concurrent
  // edit by another admin, or simply the fresher by-key membership landing
  // after the list row this was first rendered from — reset the field
  // mid-typing and closed the editor, discarding what the operator had entered
  // with no message. The pipeline they are editing has not changed, so neither
  // should their input.
  useEffect(() => {
    setEditing(false);
    setValue(pipeline.expected_cadence ?? "");
    setError(null);
    // The cadence value must NOT retrigger this — see the note above. The
    // disable has to sit immediately before the dependency array; where it was
    // it suppressed nothing, which is why it read as an unused directive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline.id]);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      // Sent trimmed; "" clears it server-side, which is how a wrong cron gets
      // removed rather than only replaced.
      const trimmed = value.trim();
      // The response carries the BUMPED version. Handing it straight back
      // matters: relying on the refetch alone means the next write reuses the
      // pre-save version and 412s — and the message blames "someone else"
      // when it was in fact this operator's own previous save.
      const updated = await updatePipeline(
        token,
        pipeline.id,
        { expected_cadence: trimmed },
        pipeline.version,
      );
      // Hold the canonical value, not what was typed — reopening the editor
      // after saving "  0 * * * *  " should show what the server actually has.
      setValue(trimmed);
      setEditing(false);
      onSaved(updated);
    } catch (e) {
      // Verbatim: the server names the cron and why it failed to parse.
      setError(e instanceof Error ? e.message : "Could not save the cadence.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <p className="mt-1 text-xs text-slate-500" data-testid="pipeline-cadence">
        {pipeline.expected_cadence ? (
          <>
            default cadence{" "}
            <span className="font-mono">{pipeline.expected_cadence}</span>
          </>
        ) : (
          <span className="text-amber-700">
            no default cadence — steps without a schedule of their own cannot be
            judged late
          </span>
        )}
        {canEdit && (
          <button
            type="button"
            className="ml-2 underline underline-offset-2"
            onClick={() => setEditing(true)}
            data-testid="edit-cadence"
          >
            {pipeline.expected_cadence ? "change" : "set one"}
          </button>
        )}
      </p>
    );
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <Input
        className="h-8 w-40 font-mono text-xs"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="0 * * * *"
        data-testid="cadence-input"
        aria-label="Expected cadence"
      />
      <Button size="sm" disabled={saving} onClick={() => void save()} data-testid="save-cadence">
        {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setEditing(false);
          setValue(pipeline.expected_cadence ?? "");
          setError(null);
        }}
      >
        Cancel
      </Button>
      <span className="text-xs text-slate-500">
        Blank clears it. A step&apos;s own cadence and a connector&apos;s own cron
        both win over this.
      </span>
      {error && (
        <span className="w-full text-xs text-red-600" data-testid="cadence-error">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * What this client runs that no pipeline is watching.
 *
 * The counterpart to the health verdict, and the reason it sits at the TOP of
 * the list rather than behind a tab: a pipeline reporting OK is read as "the
 * flow is fine", when it only ever judged the steps someone declared. Haesloop
 * declares one of its six active connectors — 480 and 484 both carry real work
 * and are covered by nothing — so this page could show all-green while most of
 * the flow went unobserved.
 *
 * Deliberately NOT a "fix it for me" button. The server reports and never
 * declares on the operator's behalf, because inferring the link measured ~30%
 * accurate against production and a wrong declaration is worse than an absent
 * one: it looks like coverage.
 */
function CoveragePanel({
  coverage,
  error,
  organisationName,
}: {
  coverage: PipelineCoverage | null;
  error: string | null;
  organisationName: string;
}) {
  if (error) {
    return (
      <Card data-testid="coverage-error">
        <CardContent className="py-4 text-sm text-red-600">
          Could not check what is uncovered: {error}
        </CardContent>
      </Card>
    );
  }
  if (!coverage) return null;

  const { covered, total, hasGap, nothingToCover } = coverageSummary(coverage);

  if (nothingToCover) {
    return (
      <Card data-testid="coverage-none">
        <CardContent className="py-4 text-sm text-slate-500">
          {organisationName} has no active connectors, so there is nothing to
          group into a pipeline yet.
        </CardContent>
      </Card>
    );
  }

  if (!hasGap) {
    return (
      <Card data-testid="coverage-clean">
        <CardContent className="flex items-center gap-2 py-4 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          All {total} active connectors are in a pipeline. A green verdict here
          covers everything this client runs.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-300" data-testid="coverage-gap">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span data-testid="coverage-headline">
            {covered} of {total} active connectors are in a pipeline
          </span>
        </CardTitle>
        <p className="text-xs text-slate-600">
          Everything below runs, and no pipeline is watching it — so it is
          absent from every health verdict on this page, rather than failing
          one.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {coverage.uncovered_connectors.length > 0 && (
          <ul className="space-y-2" data-testid="uncovered-connectors">
            {coverage.uncovered_connectors.map((c) => (
              <li
                key={c.connector_id}
                className="rounded border border-slate-200 p-2 text-sm"
                data-testid={`uncovered-connector-${c.connector_id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {c.name || `Connector ${c.connector_id}`}
                  </span>
                  <span className="font-mono text-xs text-slate-500">
                    id {c.connector_id}
                    {c.schedule ? ` · ${c.schedule}` : " · no schedule"}
                  </span>
                </div>
                {/* Server-authored: says whether declaring it is enough, or
                    whether it also needs a cadence to ever be judged late. */}
                <p className="mt-1 text-xs text-slate-600">{c.note}</p>
              </li>
            ))}
          </ul>
        )}

        {coverage.uncovered_workflows.length > 0 && (
          <div data-testid="uncovered-workflows">
            <p className="text-xs font-medium text-slate-700">
              Workflows in no pipeline
            </p>
            <ul className="mt-1 space-y-1">
              {coverage.uncovered_workflows.map((w) => (
                <li
                  key={w.workflow_key}
                  className="text-sm"
                  data-testid={`uncovered-workflow-${w.workflow_key}`}
                >
                  {w.name || w.workflow_key}{" "}
                  <span className="font-mono text-xs text-slate-500">
                    {w.workflow_key}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PipelineDetail({
  token,
  organisationId,
  pipeline,
  onBack,
  onChanged,
  canEdit,
}: {
  token: string;
  organisationId: string;
  pipeline: Pipeline;
  onBack: () => void;
  onChanged: () => void;
  canEdit: boolean;
}) {
  const [membership, setMembership] = useState<PipelineMembership | null>(null);
  const [health, setHealth] = useState<PipelineHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // No reset-on-pipeline-change effect: this component unmounts when you go
  // back to the list, and there is no detail-to-detail navigation, so `editing`
  // cannot survive into a different pipeline. Mutation testing confirmed such an
  // effect was unreachable.
  const [editing, setEditing] = useState(false);

  const { connectors, workflows, namesById } = useOrgPickers(token, organisationId);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, h] = await Promise.all([
        fetchPipelineMembership(token, pipeline.pipeline_key),
        fetchPipelineHealth(token, pipeline.id),
      ]);
      setMembership(m);
      setHealth(h);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the pipeline.");
    } finally {
      setLoading(false);
    }
  }, [token, pipeline.id, pipeline.pipeline_key]);

  useEffect(() => {
    void load();
  }, [load]);

  // Verdicts are keyed by step_order so a step's reason lands on its own row
  // even if the two calls return in different orders.
  const verdictFor = useMemo(() => {
    const map = new Map(health?.steps.map((s) => [s.step_order, s]) ?? []);
    return (order: number) => map.get(order) ?? null;
  }, [health]);

  const setStatus = async (status: "Active" | "Archived") => {
    setBusy(true);
    setError(null);
    try {
      const updated = await updatePipeline(
        token,
        pipeline.id,
        { status },
        (membership?.pipeline ?? pipeline).version,
      );
      // Same reason as the cadence save: adopt the returned version rather
      // than waiting for the refetch, so a second write in quick succession
      // cannot 412 against the operator's own change.
      setMembership((m) => (m ? { ...m, pipeline: updated } : m));
      onChanged();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update the pipeline.");
    } finally {
      setBusy(false);
    }
  };

  const retire = async () => {
    if (
      !window.confirm(
        `Delete pipeline "${pipeline.name}"? Its connectors and workflows are not affected.`,
      )
    )
      return;
    setBusy(true);
    try {
      await deletePipeline(token, pipeline.id);
      onChanged();
      onBack();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the pipeline.");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pipeline-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list">
        <ArrowLeft className="mr-1 h-4 w-4" /> All pipelines
      </Button>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>{pipeline.name}</CardTitle>
              <p className="font-mono text-xs text-slate-500">
                {pipeline.pipeline_key} · client {pipeline.client_id} ·{" "}
                {pipeline.status}
              </p>
              <CadenceEditor
                token={token}
                pipeline={membership?.pipeline ?? pipeline}
                canEdit={canEdit}
                onSaved={(updated) => {
                  // Adopt the bumped version immediately; the refetch below
                  // then reconciles everything else.
                  setMembership((m) => (m ? { ...m, pipeline: updated } : m));
                  onChanged();
                  void load();
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => void load()}
                data-testid="refresh-pipeline"
              >
                <RefreshCw className="mr-1 h-3 w-3" /> Refresh
              </Button>
              {canEdit && membership && !editing && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(true)}
                  data-testid="edit-steps"
                >
                  <Pencil className="mr-1 h-3 w-3" /> Edit steps
                </Button>
              )}
              {canEdit && pipeline.status !== "Active" && (
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => void setStatus("Active")}
                  data-testid="activate"
                >
                  Activate
                </Button>
              )}
              {canEdit && pipeline.status === "Active" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void setStatus("Archived")}
                  data-testid="archive"
                >
                  Archive
                </Button>
              )}
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void retire()}
                  data-testid="retire"
                >
                  <Trash2 className="mr-1 h-3 w-3" /> Delete
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <ErrorNote message={error} />

          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}

          {health && (
            <div
              className="space-y-1 rounded border border-slate-200 p-3"
              data-testid="pipeline-verdict"
            >
              <div className="flex items-center gap-2">
                <HealthPill health={health.health} />
                <span className="text-sm text-slate-600">
                  {/* Deliberately not "healthy/unhealthy": `healthy` is true
                      while a run is in progress, when the last outcome is
                      genuinely unknown. */}
                  {health.health === "ok" ? "Succeeding" : "Needs attention"}
                </span>
              </div>
              <p className="text-sm text-slate-700">{health.reason}</p>
            </div>
          )}

          {membership && editing && (
            <EditSteps
              token={token}
              pipelineId={pipeline.id}
              // From the membership when we have it: it is the fresher read, and
              // an If-Match built from a stale list row would fail every save.
              version={(membership?.pipeline ?? pipeline).version}
              initial={stepsToDrafts(membership.steps)}
              connectors={connectors}
              workflows={workflows}
              onCancel={() => setEditing(false)}
              onSaved={(updated) => {
                setMembership(updated);
                setEditing(false);
                onChanged();
                // Health is derived from the steps and keyed by step_order, so
                // after a renumber the old verdicts would land on the wrong
                // rows. Clear them rather than briefly mislabel, and do not
                // rely on the refetch — if `load` fails, its catch keeps the
                // stale health and the mislabelling would persist under a
                // generic error note.
                setHealth(null);
                void load();
              }}
            />
          )}

          {membership && !editing && (
            <div className="space-y-2">
              {membership.steps.map((step) => {
                const verdict = verdictFor(step.step_order);
                return (
                  <div
                    key={step.step_order}
                    className="grid gap-2 rounded border border-slate-200 p-3 md:grid-cols-12"
                    data-testid={`step-${step.step_order}`}
                  >
                    <div className="text-xs text-slate-500 md:col-span-1">
                      stage {step.stage_index}
                    </div>
                    <div className="md:col-span-2">
                      <Badge variant="outline">{step.step_type}</Badge>
                    </div>
                    <div
                      className="truncate text-xs md:col-span-4"
                      title={step.client_connector_key ?? undefined}
                    >
                      {stepTarget(step, namesById)}
                    </div>
                    <div className="md:col-span-2">
                      {verdict && <HealthPill health={verdict.health} />}
                    </div>
                    <div className="text-xs text-slate-600 md:col-span-3">
                      {verdict?.reason}
                    </div>
                  </div>
                );
              })}
              {membership.steps.length === 0 && (
                <p className="text-sm text-slate-500">
                  This pipeline has no steps, so its health answers nothing yet.
                  {canEdit ? " Use Edit steps to add some." : ""}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PipelinesAdminPage() {
  const { token } = useAuth();
  // Defence in depth. The API is the real gate — every pipeline route is
  // platform-admin only and refuses everyone else regardless of what the UI
  // renders — but the repo rule is that a CRUD surface hides what the caller
  // cannot do, so a viewer is not offered buttons that will 403.
  const { can } = usePermissions();
  const canEdit = can("edit", "connectors");
  // Platform-admin only, but still scoped to the org picker at the top of the
  // portal: a platform admin declares Haesloop's pipeline while looking at
  // Haesloop, not while typing Haesloop's internal client id from memory.
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const organisationName =
    selectedOrganisation?.organisationName ?? "this organisation";

  const [client, setClient] = useState<PipelineClientRef | null>(null);
  // "We looked and there is none" — distinct from "we could not look". Only
  // the first justifies telling an admin their org has no b2c client.
  const [clientMissing, setClientMissing] = useState(false);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [coverage, setCoverage] = useState<PipelineCoverage | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [declaring, setDeclaring] = useState(false);
  const [selected, setSelected] = useState<Pipeline | null>(null);

  // Bumped on every load. A slow response from a previous org must not land
  // after a newer one: the write path derives client_id from `client`, so a
  // stale winner would POST Haesloop's client under Rangel's org id — the
  // server stores organisation_id verbatim and never cross-checks the two.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    if (!token) return;
    if (!organisationId) {
      // No org selected (boot, or the picker cleared). Drop whatever the last
      // org left behind rather than showing it under a different heading.
      loadSeq.current += 1;
      setClient(null);
      setClientMissing(false);
      setPipelines([]);
      setCoverage(null);
      setCoverageError(null);
      setLoading(false);
      return;
    }
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const found = await fetchB2CClientForOrg(token, organisationId);
      // No client = no connectors = nothing that could be in a pipeline. Show
      // an empty list rather than every other org's pipelines.
      const rows = found
        ? await fetchPipelines(token, { clientId: found.Id })
        : [];
      if (seq !== loadSeq.current) return;
      setClient(found);
      setClientMissing(found === null);
      setPipelines(rows);

      // Deliberately NOT awaited, and deliberately outside `loading`.
      //
      // It answers a different question from the list, and it is the slowest
      // of the three calls — a live NOLOCK query across the shared b2c
      // connector table. Awaiting it here would put the pipeline rows, already
      // in hand, behind it: a stalled coverage query left the operator on
      // "Loading…" indefinitely with nothing rendered. Failure is reported in
      // its own card for the same reason.
      //
      // Still `seq`-guarded, so a slow answer for Haesloop can never land
      // under Rangel's heading.
      if (!found) {
        setCoverage(null);
        setCoverageError(null);
      } else {
        setCoverage(null);
        setCoverageError(null);
        void (async () => {
          try {
            const cov = await fetchPipelineCoverage(
              token,
              found.Id,
              organisationId,
            );
            if (seq !== loadSeq.current) return;
            setCoverage(cov);
            setCoverageError(null);
          } catch (e) {
            if (seq !== loadSeq.current) return;
            setCoverage(null);
            setCoverageError(
              e instanceof Error ? e.message : "coverage request failed",
            );
          }
        })();
      }
    } catch (e) {
      if (seq !== loadSeq.current) return;
      // Drop the previous org's answers as well as reporting the failure.
      // `loading` masks them on the happy path, and the mask is exactly what
      // disappears here — leaving Haesloop's client and rows on screen under
      // Rangel's heading, with Declare enabled and holding client 165.
      setClient(null);
      setClientMissing(false);
      setPipelines([]);
      setCoverage(null);
      setCoverageError(null);
      setError(e instanceof Error ? e.message : "Could not load pipelines.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [token, organisationId]);

  // Switching org drops you back to that org's list: a half-filled Haesloop
  // declaration must not carry over to Rangel, and an open Haesloop pipeline
  // must not stay on screen labelled as Rangel's. The platform layout pins a
  // fixed React `key`, so this page is never remounted — nothing else does it.
  //
  // Only these two. `load` re-enters its loading state on the same commit, so
  // the list and the Declare button are already suppressed while the new org
  // resolves; clearing them here as well would be untestable duplication.
  useEffect(() => {
    setDeclaring(false);
    setSelected(null);
  }, [organisationId]);

  useEffect(() => {
    void load();
  }, [load]);


  if (!token) return null;

  if (selected) {
    return (
      <div className="p-6">
        <PipelineDetail
          token={token}
          organisationId={organisationId}
          pipeline={selected}
          onBack={() => setSelected(null)}
          onChanged={() => void load()}
          canEdit={canEdit}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6" data-testid="pipelines-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <GitBranch className="h-6 w-6" /> Pipelines
          </h1>
          <p className="text-sm text-slate-600">
            A pipeline names the connectors and workflows that make up one
            flow, and gives it a single health answer. Showing{" "}
            <strong data-testid="scoped-org">{organisationName}</strong>.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setDeclaring(true)}
            disabled={loading || !client}
            title={
              clientMissing
                ? `${organisationName} has no b2c client, so it has no connectors to group.`
                : undefined
            }
            data-testid="new-pipeline"
          >
            <Plus className="mr-1 h-4 w-4" /> Declare pipeline
          </Button>
        )}
      </div>

      <ErrorNote message={error} />

      {declaring && canEdit && (
        <DeclareForm
          token={token}
          organisationId={organisationId}
          organisationName={organisationName}
          client={client}
          onCancel={() => setDeclaring(false)}
          onCreated={() => {
            setDeclaring(false);
            void load();
          }}
        />
      )}

      {/* Above the list on purpose. A connector nobody declared appears in no
          row below, so putting this underneath would hide the one thing on
          this page you cannot otherwise discover. */}
      {!loading && (
        <CoveragePanel
          coverage={coverage}
          error={coverageError}
          organisationName={organisationName}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : pipelines.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">
            {!organisationId
              ? "Select an organisation to see its pipelines."
              : clientMissing
                ? `${organisationName} has no b2c client, so there are no connectors to group into a pipeline.`
                : client
                  ? `No pipelines declared for ${organisationName} yet. A pipeline groups the connectors and workflows of one flow so you can ask "did it work?" once.`
                  : `Could not read ${organisationName}'s pipelines.`}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {pipelines.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              className="w-full rounded border border-slate-200 p-3 text-left hover:bg-slate-50"
              data-testid={`pipeline-${p.pipeline_key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="font-mono text-xs text-slate-500">
                    {p.pipeline_key} · client {p.client_id}
                  </div>
                </div>
                <Badge variant="outline">{p.status}</Badge>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
